// Context that supplies the "Insert dynamic…" picker with:
//   - the active brand profile (so we can list $brand.X paths and resolve
//     each to a concrete value for preview)
//   - the current section's slot schema (to list in the Slot tab)
//   - a callback to create a new slot (so the picker can add one inline)
//
// Available in every mode. In document mode the Slot tab is empty; in
// section mode the section editor wires slot state in via props.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { SlotDefault, SlotSchema } from '@nexmail/shared-util/email-builder';

export interface DynamicPickerSlotEntry {
  name: string;
  schema: SlotSchema;
  default: SlotDefault;
}

export interface DynamicPickerBrand {
  name?: string;
  description?: string;
  tagline?: string;
  slogan?: string;
  website?: string;
  email?: string;
  address?: string;
  industry?: string;
  logoUrl?: string;
  // CSS-ready brand radius (e.g. "6px", "12px", "9999px"). Exposed so
  // buttons/cards can bind to $brand.borderRadius.
  borderRadius?: string;
  // Semantic color tokens keyed by usage.
  colorByUsage: Record<string, string>;
  // Semantic font tokens keyed by usage.
  fontByUsage: Record<string, string>;
  // Social links keyed by platform.
  socialByPlatform: Record<string, string>;
  // Brand nav links (scraped or manually authored). Consumed by the section
  // library resolver when a section declares a nav-links slot with strategy
  // 'brand-nav-links'.
  navLinks?: Array<{ label: string; href: string }>;
  // Connected-store collections, ordered for indexed merge tag resolution
  // (`$brand.collections[N].title|url|imageUrl`). URL is pre-derived as
  // `${website}/collections/${handle}` server-side. Falls through to the
  // system_brand_defaults curated list when the active brand has none.
  collections?: Array<{ title: string; url: string; imageUrl?: string }>;
}

export interface DynamicPickerContextValue {
  brand: DynamicPickerBrand | null;
  slots: DynamicPickerSlotEntry[];
  // Add a new slot. Returns the final slot name (may differ from input if a
  // collision required disambiguation). Picker inserts `$slot.<name>` at the
  // caller's cursor after this resolves.
  onRequestAddSlot?: (slot: DynamicPickerSlotEntry) => string;
  // Optional placeholder resolver used by read-only preview surfaces (e.g.
  // canvas-section thumbnails) so $slot.X tokens render as a meaningful
  // placeholder while authoring. Saved docs always keep raw tokens — only
  // the rendered preview consumes this. Wired by EmailBuilder when the
  // active mode controller exposes a synthetic preview brand.
  previewSlotResolver?: (slotName: string) => string | undefined;
}

const DynamicPickerContext = createContext<DynamicPickerContextValue | null>(null);

export function DynamicPickerProvider({
  value,
  children,
}: {
  value: DynamicPickerContextValue;
  children: ReactNode;
}) {
  // Memoize so consumers don't re-render on unrelated parent renders.
  const memo = useMemo(() => value, [value.brand, value.slots, value.onRequestAddSlot, value.previewSlotResolver]); // eslint-disable-line react-hooks/exhaustive-deps
  return <DynamicPickerContext.Provider value={memo}>{children}</DynamicPickerContext.Provider>;
}

export function useDynamicPicker(): DynamicPickerContextValue | null {
  return useContext(DynamicPickerContext);
}
